import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConnectionCard = ({ 
  platform, 
  isConnected, 
  userInfo, 
  onConnect, 
  onDisconnect, 
  isConnecting,
  handleRefresh
}) => {
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const getPlatformConfig = () => {
    switch (platform) {
      case 'spotify':
        return {
          name: 'Spotify',
          icon: 'Music',
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          description: 'Connect your Spotify account to sync playlists from the world\'s largest music streaming platform.',
          features: ['Access to 70+ million songs', 'Personal playlists', 'Liked songs', 'Discover Weekly']
        };
      case 'youtube':
        return {
          name: 'YouTube Music',
          icon: 'Play',
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          description: 'Connect your YouTube Music account to sync playlists and access your music library.',
          features: ['Official music videos', 'Live performances', 'Covers & remixes', 'Personal uploads']
        };
      default:
        return {
          name: platform,
          icon: 'Link',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20',
          description: 'Connect your account to enable playlist synchronization.',
          features: []
        };
    }
  };

  const config = getPlatformConfig();

  const handleDisconnect = () => {
    onDisconnect(platform);
    setShowDisconnectConfirm(false);
  };

  return (
    <div className={`relative p-6 bg-surface border rounded-xl shadow-elevation-1 transition-all duration-300 hover:shadow-elevation-2 ${
      isConnected ? `${config.borderColor} ${config.bgColor}` : 'border-border hover:border-primary/30'
    }`}>
      {/* Connection Status Indicator */}
      {isConnected && (
        <div className="absolute top-4 right-4">
          <div className={`flex items-center space-x-1 px-2 py-1 ${config.bgColor} rounded-full`}>
            <div className={`w-2 h-2 bg-current rounded-full ${config.color}`}></div>
            <span className={`text-xs font-medium ${config.color}`}>Connected</span>
          </div>
        </div>
      )}

      {/* Platform Header */}
      <div className="flex items-center space-x-4 mb-4">
        <div className={`flex items-center justify-center w-12 h-12 ${config.bgColor} rounded-xl`}>
          <Icon name={config.icon} size={24} className={config.color} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{config.name}</h3>
          <p className="text-sm text-text-secondary">Music Streaming Platform</p>
        </div>
      </div>

      {/* Connection Status */}
      {isConnected && userInfo ? (
        <div className="mb-4 p-4 bg-background rounded-lg border border-border">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-surface-secondary rounded-full flex items-center justify-center">
              <Icon name="User" size={20} className="text-text-secondary" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">@{userInfo.username}</p>
              <p className="text-xs text-text-secondary">Connected {userInfo.connectedAt}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-text-muted">
            <span>Last sync: {userInfo.lastSync}</span>
            <span className={`${config.color} font-medium`}>Active</span>
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <p className="text-sm text-text-secondary mb-3">{config.description}</p>
          
          {/* Features List */}
          <div className="space-y-2">
            {config.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={16} className={config.color} />
                <span className="text-xs text-text-muted">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {isConnected ? (
          <>
            {!showDisconnectConfirm ? (
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="flex-1 text-error hover:text-error hover:bg-error/10"
                  iconName="Unlink"
                  iconSize={16}
                >
                  Disconnect
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  iconName="RefreshCw"
                  iconSize={16}
                  onClick={()=>handleRefresh(platform)}
                >
                  Refresh 
                </Button> 
              </div>
            ) : (
              <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error mb-3">
                  Are you sure you want to disconnect your {config.name} account?
                </p>
                <div className="flex space-x-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDisconnect}
                    className="flex-1"
                  >
                    Yes, Disconnect
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <Button
            variant="primary"
            size="lg"
            onClick={() => onConnect(platform)}
            disabled={isConnecting}
            loading={isConnecting}
            className="w-full"
            iconName="Link"
            iconSize={20}
          >
            {isConnecting ? 'Connecting...' : `Connect ${config.name}`}
          </Button>
        )}
      </div>

      {/* Security Notice */}
      {!isConnected && (
        <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Shield" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-primary font-medium mb-1">Secure OAuth Connection</p>
              <p className="text-xs text-text-muted">
                We use official OAuth to securely connect your account. We never store your passwords.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionCard;